/**
 * <load-more-products> — appends the next page of a paginated product grid
 * in place instead of navigating to a new page. Fetches the next page's
 * section fragment via Shopify's standard ?section_id= trick (same approach
 * Dawn's own facets.js uses for filter/sort), pulls the new product items
 * out of it, and swaps in whatever "next" URL that fragment reports (or
 * removes itself once there's no further page).
 */
class LoadMoreProducts extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.load-more__button');
    this.sectionId = this.dataset.sectionId;
    this.targetId = this.dataset.target || 'product-grid';
    if (!this.button) return;
    this.button.addEventListener('click', this.onClick.bind(this));
  }

  async onClick(event) {
    event.preventDefault();
    if (this.loading) return;

    const nextUrl = this.button.dataset.nextUrl || this.button.getAttribute('href');
    if (!nextUrl) return;

    this.loading = true;
    this.classList.add('load-more--loading');

    const separator = nextUrl.includes('?') ? '&' : '?';
    const fetchUrl = `${nextUrl}${separator}section_id=${this.sectionId}`;

    try {
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error(`Load more failed: ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newList = doc.getElementById(this.targetId);
      const currentList = document.getElementById(this.targetId);
      if (newList && currentList) {
        const fragment = document.createDocumentFragment();
        Array.from(newList.children).forEach((item) => fragment.appendChild(item));
        currentList.appendChild(fragment);
      }

      const newButton = doc.querySelector('load-more-products .load-more__button');
      if (newButton && newButton.dataset.nextUrl) {
        this.button.dataset.nextUrl = newButton.dataset.nextUrl;
        this.button.setAttribute('href', newButton.dataset.nextUrl);
      } else {
        // No further page in the fetched fragment — this was the last one.
        this.remove();
      }
    } catch (err) {
      // Fall back to a real page navigation rather than leaving the button dead.
      window.location.href = nextUrl;
    } finally {
      this.loading = false;
      this.classList.remove('load-more--loading');
    }
  }
}

customElements.define('load-more-products', LoadMoreProducts);
